import {
  CATEGORY_PICTURES,
  PRODUCT_PICTURES,
  RECIPE_PICTURES,
  type FoodPicture,
} from '../../data/food-pictures';
import { STAPLE_PRODUCT_IDS } from '../../data/staples';
import type { Recipe } from '../../domain/recipe';
import type { Product } from '../../domain/types';

const MAX_INGREDIENT_PICTURES = 2;

/**
 * DE: Adresse einer Bilddatei; relativ wie `base: './'` in vite.config.ts, damit die App auch unter
 * einem Unterpfad läuft.
 * EN: Address of a picture file; relative like `base: './'` in vite.config.ts, so the app also
 * works under a sub-path.
 */
export function pictureUrl(picture: FoodPicture): string {
  return `./food/${picture}.svg`;
}

/**
 * DE: Gericht-Bild eines Rezepts; eigene Rezepte zeigen einen Topf.
 * EN: Dish picture of a recipe; own recipes show a pot.
 */
export function recipePicture(recipeId: string): FoodPicture {
  return RECIPE_PICTURES[recipeId] ?? 'pot-of-food';
}

/**
 * DE: Eigenes Bild eines Produkts, falls es eines hat.
 * EN: A product's own picture, if it has one.
 */
export function ownProductPicture(productId: string): FoodPicture | undefined {
  return PRODUCT_PICTURES[productId];
}

/**
 * DE: Bild eines Produkts: das eigene, sonst das seiner Kategorie.
 * EN: Picture of a product: its own, otherwise its category's.
 */
export function productPicture(product: Product): FoodPicture {
  return ownProductPicture(product.id) ?? CATEGORY_PICTURES[product.category];
}

/**
 * DE: Bis zu zwei Zutatenbilder, die zum Gericht-Bild etwas hinzufügen: keine Grundzutaten, keine
 * Zutaten ohne eigenes Bild, kein Bild doppelt.
 * EN: Up to two ingredient pictures that add to the dish picture: no staples, no ingredients
 * without a picture of their own, no picture twice.
 */
export function ingredientPictures(recipe: Recipe): FoodPicture[] {
  const taken = new Set<FoodPicture>([recipePicture(recipe.id)]);
  const pictures: FoodPicture[] = [];
  for (const { productId } of recipe.ingredients) {
    const picture = ownProductPicture(productId);
    if (!picture || taken.has(picture) || STAPLE_PRODUCT_IDS.includes(productId)) continue;
    taken.add(picture);
    pictures.push(picture);
    if (pictures.length === MAX_INGREDIENT_PICTURES) break;
  }
  return pictures;
}
